import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { CountryCode } from 'libphonenumber-js';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { NotificationService } from 'ui-components/services/notification-service';
import { Input, version2 } from 'ui-components/components/input';

import { messages } from './messages';
import { SellerPhoneContactReadonlyDisplay } from './phone-contact-readonly';
import { checkIsErrorMessageUserCancelledMfa } from '../../../../../global/step-up-authentication/helpers';
import { LogService } from '../../../../../services/log-service';
import { Segment } from '../../../../../services/analytics-services/segment';
import { PHONE_CAPTURE_SETTINGS_SAVE } from '../../../../../constants/analytics-events';
import Global from '../../../../../store/global-selectors';
import { PhoneNumberCaptureForm } from '../../../../../components/phone-number-capture/phone-number-capture-form';
import { usePhoneConsent } from '../../../../../components/phone-number-capture/use-phone-consent';
import { useSavePhoneNumber } from '../../../../../components/phone-number-capture/use-save-phone-number';
import { US } from '../../../../../constants/country-codes';

export const PhoneContactRow = () => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const initialSellerPhoneNumber = useSelector(Global.selectSellerPhoneNumber) ?? '';
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const countryCode: CountryCode = (sellerCountry as CountryCode) || US;
  const [phoneNumber, setPhoneNumber] = useState<string>(initialSellerPhoneNumber);
  const { hasConsent, isLoading: isConsentLoading } = usePhoneConsent();
  const initialConsentState = initialSellerPhoneNumber ? hasConsent : true;
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(initialConsentState);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditing && !isConsentLoading) {
      const consentState = initialSellerPhoneNumber ? hasConsent : true;
      setIsConsentChecked(consentState);
    }
  }, [isEditing, hasConsent, isConsentLoading, initialSellerPhoneNumber]);

  const handleSaveSuccess = useCallback(() => {
    Segment.trackEvent(PHONE_CAPTURE_SETTINGS_SAVE);
    setIsEditing(false);
  }, []);

  const handleSaveError = useCallback((error: unknown) => {
    const isFailedMfaError = checkIsErrorMessageUserCancelledMfa(error);
    if (!isFailedMfaError) {
      LogService.logError(new Error('Error saving phone contact', { cause: error }));
    }
    NotificationService.showGenericFailureNotification();
  }, []);

  const { savePhoneNumber, isSaving } = useSavePhoneNumber({
    onSuccess: handleSaveSuccess,
    onError: handleSaveError,
  });

  const handlePhoneChange = useCallback((value: string) => {
    setPhoneNumber(value);
  }, []);

  const handleConsentChange = useCallback((checked: boolean) => {
    setIsConsentChecked(checked);
  }, []);

  const handleValidationChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const handleSave = useCallback(() => {
    savePhoneNumber({ phoneNumber, isConsentChecked, countryCode });
  }, [isConsentChecked, phoneNumber, savePhoneNumber, countryCode]);

  const enterEditMode = useCallback(() => setIsEditing(true), []);

  const handleCancel = useCallback(() => {
    setPhoneNumber(initialSellerPhoneNumber);
    const resetConsentState = initialSellerPhoneNumber ? hasConsent : true;
    setIsConsentChecked(resetConsentState);
    setIsEditing(false);
  }, [initialSellerPhoneNumber, hasConsent]);

  const isConsentDisabled = !isEditing;
  const isSaveDisabled = isSaving || !isFormValid;

  const renderPhoneInput = () => {
    if (isEditing) {
      return (
        <PhoneNumberCaptureForm
          countryCode={countryCode}
          initialPhoneNumber={initialSellerPhoneNumber}
          isConsentChecked={isConsentChecked}
          isConsentDisabled={isConsentDisabled}
          isDisabled={isSaving}
          onConsentChange={handleConsentChange}
          onPhoneChange={handlePhoneChange}
          onValidationChange={handleValidationChange}
          shouldShowConsent={false}
          shouldShowLabel={false}
        />
      );
    }

    if (initialSellerPhoneNumber) {
      return <SellerPhoneContactReadonlyDisplay sellerPhoneNumber={initialSellerPhoneNumber} />;
    }

    return <Input disabled name='phone-readonly' value='' version={version2} />;
  };

  return (
    <FlexColumn gap={Gap.Space8}>
      <Text textType={TextType.Label} value={messages.phoneInputLabel} />
      <FlexRow gap={Gap.Space8}>
        {renderPhoneInput()}
        {isEditing ? (
          <>
            <Button disabled={isSaving} onClick={handleCancel} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
            <Button disabled={isSaveDisabled} onClick={handleSave} theme={THEME_CONSTANTS.primary}>
              {isSaving ? <FormattedMessage {...messages.saving} /> : <FormattedMessage {...messages.saveButton} />}
            </Button>
          </>
        ) : (
          <Button onClick={enterEditMode} theme={THEME_CONSTANTS.secondary}>
            <FormattedMessage {...messages.editButton} />
          </Button>
        )}
      </FlexRow>
      <Checkbox
        checked={isConsentChecked}
        disabled={isConsentDisabled}
        label={intl.formatMessage(messages.consentCheckboxLabel)}
        onChange={handleConsentChange}
      />
    </FlexColumn>
  );
};
